import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../components/layout/Layout';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import api from '../api';

const COLORS = {
  forestGreen: '#0F3D2E',
  deepGreen: '#003C3C',
  heritageGold: '#C9A14A',
  offWhite: '#FFFDF7',
  sand: '#F7F3EA',
  border: '#E4D7BC',
  success: '#2F8F4E',
  danger: '#B54135',
  warning: '#C9A14A',
  muted: '#746A5B',
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const vehicleLabel = (value) => {
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'bike' || normalized === 'motorcycle') {
    return 'Motorbike / Motorcycle';
  }
  if (normalized === 'bicycle') return 'Bicycle';
  if (normalized === 'car') return 'Car';
  return value || 'Not provided';
};
const EMPTY_RIDER_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  vehicleType: 'motorcycle',
  temporaryPassword: '',
};

const normalizeLicenceUrl = (url) => {
  if (!url) return '';

  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'media.yabathoplatform.co.za') {
      parsed.pathname = parsed.pathname.replace(/^\/media\//, '/');
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

export default function Riders() {
  const [riders, setRiders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selectedRider, setSelectedRider] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [riderForm, setRiderForm] = useState(EMPTY_RIDER_FORM);
  const [editingRider, setEditingRider] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    loadRiders();
  }, []);

  const loadRiders = async () => {
    try {
      setLoading(true);
      const response = await api.fetchRiders();
      const rawList = response?.data?.data || [];

      const formatted = rawList.map((rider) => ({
        id: rider.id,
        name: `${rider.firstName} ${rider.lastName}`,
        phone: rider.phone,
        rider_type: vehicleLabel(rider.vehicleType),
        status: rider.isAccountActive ? 'Active' : 'Suspended',
        accountActive: rider.isAccountActive,
        isApproved: rider.isApproved,
        approvalStatus:
          rider.approvalStatus || (rider.isApproved ? 'approved' : 'pending'),
        email: rider.email,
        licenseUrl: normalizeLicenceUrl(rider.licenseUrl),
      }));

      setRiders(formatted);
    } catch (error) {
      console.error('Failed to fetch riders:', error);
      setNotice({
        type: 'error',
        text: error.message || 'Could not load riders.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const updateRiderForm = (field, value) => {
    setRiderForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreateRider = async (event) => {
    event.preventDefault();

    try {
      setCreateLoading(true);
      setNotice(null);
      const payload = {
        ...riderForm,
        firstName: riderForm.firstName.trim(),
        lastName: riderForm.lastName.trim(),
        email: riderForm.email.trim().toLowerCase(),
        phone: riderForm.phone.trim(),
      };
      await api.createRider(payload);
      await loadRiders();
      setCreateModalOpen(false);
      setRiderForm(EMPTY_RIDER_FORM);
      setNotice({
        type: 'success',
        text: `Rider account created for ${riderForm.email}. Share the temporary credentials securely.`,
      });
    } catch (error) {
      setNotice({
        type: 'error',
        text: error.message || 'Could not create the rider account.',
      });
    } finally {
      setCreateLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return riders.filter((rider) => {
      if (statusFilter && rider.status !== statusFilter) return false;
      if (approvalFilter && rider.approvalStatus !== approvalFilter) return false;
      if (!term) return true;

      return [
        rider.name,
        rider.phone,
        rider.email,
        rider.rider_type,
        rider.status,
        rider.approvalStatus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [riders, searchTerm, statusFilter, approvalFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedRiders = filtered.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );
  const activeCount = riders.filter((rider) => rider.status === 'Active').length;
  const suspendedCount = riders.filter((rider) => rider.status === 'Suspended').length;
  const approvedCount = riders.filter((rider) => rider.approvalStatus === 'approved').length;
  const pendingCount = riders.filter((rider) => rider.approvalStatus === 'pending').length;
  const rejectedCount = riders.filter((rider) => rider.approvalStatus === 'rejected').length;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const performToggleStatus = async (rider) => {
    const nextActive = rider.status !== 'Active';

    try {
      setActionId(`status-${rider.id}`);
      setNotice(null);
      await api.toggleStatus(rider.id, nextActive);
      await loadRiders();
      setNotice({
        type: 'success',
        text: `${rider.name} has been ${nextActive ? 'activated' : 'suspended'}.`,
      });
    } catch (error) {
      console.error('Failed to toggle rider status:', error);
      setNotice({
        type: 'error',
        text: error.message || 'Could not update rider status.',
      });
    } finally {
      setActionId(null);
      setConfirmAction(null);
    }
  };

  const performApproval = async (riderId, approve = true) => {
    const rider = riders.find((item) => item.id === riderId);

    try {
      setActionId(`${approve ? 'approve' : 'reject'}-${riderId}`);
      setNotice(null);
      await api.updateRiderApproval(riderId, approve);
      await loadRiders();
      setNotice({
        type: 'success',
        text: `${rider?.name || 'Rider'} has been ${approve ? 'approved' : 'rejected'}.`,
      });
    } catch (error) {
      console.error('Failed to update rider approval:', error);
      setNotice({
        type: 'error',
        text: error.message || 'Could not update rider approval.',
      });
    } finally {
      setActionId(null);
      setConfirmAction(null);
    }
  };

  const openRiderEditor = (rider) => {
    const [firstName = '', ...lastNameParts] = rider.name.split(' ');
    setEditingRider({
      id: rider.id,
      firstName,
      lastName: lastNameParts.join(' '),
      email: rider.email || '',
      phone: rider.phone || '',
      vehicleType: String(rider.rider_type || '').toLowerCase().includes('bicycle')
        ? 'bicycle'
        : String(rider.rider_type || '').toLowerCase() === 'car'
        ? 'car'
        : 'motorcycle',
    });
  };

  const handleUpdateRider = async (event) => {
    event.preventDefault();
    try {
      setEditLoading(true);
      setNotice(null);
      await api.updateRider(editingRider.id, {
        ...editingRider,
        firstName: editingRider.firstName.trim(),
        lastName: editingRider.lastName.trim(),
        email: editingRider.email.trim().toLowerCase(),
        phone: editingRider.phone.trim(),
      });
      await loadRiders();
      setEditingRider(null);
      setNotice({ type: 'success', text: 'Rider details saved permanently.' });
    } catch (error) {
      setNotice({ type: 'error', text: error.message || 'Could not update the rider.' });
    } finally {
      setEditLoading(false);
    }
  };

  const performReopenApproval = async (riderId) => {
    const rider = riders.find((item) => item.id === riderId);

    try {
      setActionId(`reopen-${riderId}`);
      setNotice(null);
      await api.updateRiderApproval(riderId, false, 'pending');
      await loadRiders();
      setNotice({
        type: 'success',
        text: `${rider?.name || 'Rider'} can now resubmit onboarding details for review.`,
      });
    } catch (error) {
      console.error('Failed to reopen rider approval:', error);
      setNotice({
        type: 'error',
        text: error.message || 'Could not reopen rider approval.',
      });
    } finally {
      setActionId(null);
      setConfirmAction(null);
    }
  };

  const requestStatusChange = (rider) => {
    const nextActive = rider.status !== 'Active';
    setConfirmAction({
      title: `${nextActive ? 'Activate' : 'Suspend'} rider`,
      message: `${nextActive ? 'Activate' : 'Suspend'} ${rider.name}? ${
        nextActive
          ? 'They will be able to use their rider account again.'
          : 'They will be logged out and delivery actions will be locked.'
      }`,
      confirmLabel: nextActive ? 'Activate rider' : 'Suspend rider',
      tone: nextActive ? 'success' : 'danger',
      onConfirm: () => performToggleStatus(rider),
    });
  };

  const requestApprovalChange = (rider, approve) => {
    setConfirmAction({
      title: `${approve ? 'Approve' : 'Reject'} rider`,
      message: `${approve ? 'Approve' : 'Reject'} ${rider.name}? ${
        approve
          ? 'They will be able to go available when their account is active.'
          : 'Their rider app will show that the application was rejected.'
      }`,
      confirmLabel: approve ? 'Approve rider' : 'Reject rider',
      tone: approve ? 'success' : 'danger',
      onConfirm: () => performApproval(rider.id, approve),
    });
  };

  const requestReopenApproval = (rider) => {
    setConfirmAction({
      title: 'Allow rider resubmission',
      message: `Move ${rider.name} back to pending review? They can update their onboarding details and submit them again from the rider app.`,
      confirmLabel: 'Allow resubmission',
      tone: 'success',
      onConfirm: () => performReopenApproval(rider.id),
    });
  };

  const viewRider = (rider) => {
    setSelectedRider(rider);
  };

  if (loading)
    return (
      <Layout>
        <p>Loading riders...</p>
      </Layout>
    );

  return (
    <Layout>
      <PageHeader
        eyebrow="Fleet"
        title="Riders"
        subtitle="Create rider accounts, review onboarding, and control approval."
      />

      <div style={styles.primaryActions}>
        <button
          type="button"
          style={styles.secondaryActionButton}
          onClick={loadRiders}
        >
          Refresh Riders
        </button>
        <button
          type="button"
          style={styles.createButton}
          onClick={() => setCreateModalOpen(true)}
        >
          Create Rider
        </button>
      </div>

      <div style={styles.toolbar}>
        <input
          type="search"
          placeholder="Search riders by name, phone, email, type..."
          value={searchTerm}
          onChange={handleSearch}
          style={styles.searchInput}
        />
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setPage(1);
          }}
          style={styles.selectInput}
        >
          <option value="">All accounts ({riders.length})</option>
          <option value="Active">Active ({activeCount})</option>
          <option value="Suspended">Suspended ({suspendedCount})</option>
        </select>
        <select
          value={approvalFilter}
          onChange={(event) => {
            setApprovalFilter(event.target.value);
            setPage(1);
          }}
          style={styles.selectInput}
        >
          <option value="">All approvals</option>
          <option value="approved">Approved ({approvedCount})</option>
          <option value="pending">Pending ({pendingCount})</option>
          <option value="rejected">Rejected ({rejectedCount})</option>
        </select>
        <select
          value={pageSize}
          onChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(1);
          }}
          style={styles.selectInput}
        >
          {PAGE_SIZE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option} per page
            </option>
          ))}
        </select>
      </div>

      <div style={styles.resultMeta}>
        Showing {filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1}-
        {Math.min(safePage * pageSize, filtered.length)} of {filtered.length}{' '}
        riders
      </div>

      {notice && (
        <div
          style={{
            ...styles.notice,
            ...(notice.type === 'error' ? styles.noticeError : styles.noticeSuccess),
          }}>
          {notice.text}
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <strong>No riders found.</strong>
          <p>Try changing the search, account status, or approval dropdown.</p>
        </div>
      ) : (
        <>
          <div style={styles.tableShell}>
            <table style={styles.table}>
              <thead style={styles.thead}>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Rider Type</th>
                  <th style={styles.th}>Licence</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Approved</th>
                  <th style={{ ...styles.th, ...styles.actionsHeader }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRiders.map((rider, index) => (
                  <tr
                    key={rider.id}
                    style={index % 2 === 0 ? styles.evenRow : styles.oddRow}
                  >
                    <td style={styles.td}>{rider.name}</td>
                    <td style={styles.td}>{rider.phone || '-'}</td>
                    <td style={styles.td}>{rider.rider_type || '-'}</td>
                    <td style={styles.td}>
                      {rider.licenseUrl ? (
                        <button
                          type="button"
                          style={styles.documentButton}
                          onClick={() => viewRider(rider)}
                        >
                          View licence
                        </button>
                      ) : (
                        <span style={styles.missingDocument}>Not uploaded</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.status,
                          backgroundColor:
                            rider.status === 'Active' ? COLORS.success : COLORS.danger,
                        }}
                      >
                        {rider.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span
                        style={{
                          ...styles.status,
                          backgroundColor:
                            rider.approvalStatus === 'approved'
                              ? COLORS.success
                              : rider.approvalStatus === 'rejected'
                              ? COLORS.danger
                              : COLORS.warning,
                        }}
                      >
                        {rider.approvalStatus === 'approved'
                          ? 'Approved'
                          : rider.approvalStatus === 'rejected'
                          ? 'Rejected'
                          : 'Pending'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionGroup}>
                        <button
                          style={styles.actionBtn}
                          onClick={() => viewRider(rider)}
                        >
                          View
                        </button>
                        <button style={styles.actionBtn} onClick={() => openRiderEditor(rider)}>
                          Edit
                        </button>
                        <button
                          style={{
                            ...styles.actionBtn,
                            backgroundColor:
                              rider.status === 'Active' ? COLORS.heritageGold : COLORS.success,
                          }}
                          onClick={() => requestStatusChange(rider)}
                          disabled={actionId === `status-${rider.id}`}
                        >
                          {actionId === `status-${rider.id}`
                            ? 'Saving...'
                            : rider.status === 'Active'
                            ? 'Suspend'
                            : 'Activate'}
                        </button>
                        {rider.approvalStatus === 'rejected' ? (
                          <button
                            style={{
                              ...styles.actionBtn,
                              backgroundColor: COLORS.success,
                            }}
                            onClick={() => requestReopenApproval(rider)}
                            disabled={actionId === `reopen-${rider.id}`}
                          >
                            {actionId === `reopen-${rider.id}`
                              ? 'Saving...'
                              : 'Allow resubmission'}
                          </button>
                        ) : rider.approvalStatus !== 'approved' && (
                          <>
                            <button
                              style={{
                                ...styles.actionBtn,
                                backgroundColor: COLORS.success,
                              }}
                              onClick={() => requestApprovalChange(rider, true)}
                              disabled={
                                actionId === `approve-${rider.id}` ||
                                !rider.licenseUrl
                              }
                              title={
                                rider.licenseUrl
                                  ? 'Approve rider'
                                  : 'Rider must complete onboarding first'
                              }
                            >
                              {actionId === `approve-${rider.id}`
                                ? 'Saving...'
                                : rider.licenseUrl
                                ? 'Approve'
                                : 'Awaiting onboarding'}
                            </button>
                            <button
                              style={{
                                ...styles.actionBtn,
                                backgroundColor: COLORS.danger,
                              }}
                              onClick={() => requestApprovalChange(rider, false)}
                              disabled={actionId === `reject-${rider.id}`}
                            >
                              {actionId === `reject-${rider.id}` ? 'Saving...' : 'Reject'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={styles.paginationBar}>
            <button
              type="button"
              style={styles.pageButton}
              disabled={safePage === 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              Previous
            </button>
            <span style={styles.pageText}>
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              style={styles.pageButton}
              disabled={safePage === totalPages}
              onClick={() =>
                setPage((current) => Math.min(totalPages, current + 1))
              }
            >
              Next
            </button>
          </div>
        </>
      )}

      {createModalOpen && (
        <div style={styles.modalOverlay} onClick={() => setCreateModalOpen(false)}>
          <form
            style={styles.modal}
            onSubmit={handleCreateRider}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <p style={styles.modalEyebrow}>Fleet Access</p>
                <h3 style={styles.modalTitle}>Create Rider Account</h3>
              </div>
              <button
                type="button"
                style={styles.closeButton}
                onClick={() => setCreateModalOpen(false)}
              >
                Close
              </button>
            </div>

            <div style={styles.formGrid}>
              <label style={styles.formField}>
                <span>First name</span>
                <input
                  required
                  minLength={2}
                  maxLength={50}
                  value={riderForm.firstName}
                  onChange={(event) =>
                    updateRiderForm('firstName', event.target.value)
                  }
                  style={styles.formInput}
                />
              </label>
              <label style={styles.formField}>
                <span>Last name</span>
                <input
                  required
                  minLength={2}
                  maxLength={50}
                  value={riderForm.lastName}
                  onChange={(event) =>
                    updateRiderForm('lastName', event.target.value)
                  }
                  style={styles.formInput}
                />
              </label>
              <label style={styles.formField}>
                <span>Email</span>
                <input
                  required
                  type="email"
                  value={riderForm.email}
                  onChange={(event) =>
                    updateRiderForm('email', event.target.value)
                  }
                  style={styles.formInput}
                />
              </label>
              <label style={styles.formField}>
                <span>Phone</span>
                <input
                  required
                  type="tel"
                  minLength={8}
                  maxLength={20}
                  value={riderForm.phone}
                  onChange={(event) =>
                    updateRiderForm('phone', event.target.value)
                  }
                  style={styles.formInput}
                />
              </label>
              <label style={styles.formField}>
                <span>Vehicle</span>
                <select
                  value={riderForm.vehicleType}
                  onChange={(event) =>
                    updateRiderForm('vehicleType', event.target.value)
                  }
                  style={styles.formInput}
                >
                  <option value="motorcycle">Motorcycle</option>
                  <option value="bicycle">Bicycle</option>
                  <option value="car">Car</option>
                </select>
              </label>
              <label style={styles.formField}>
                <span>Temporary password</span>
                <input
                  required
                  type="password"
                  minLength={8}
                  maxLength={72}
                  autoComplete="new-password"
                  value={riderForm.temporaryPassword}
                  onChange={(event) =>
                    updateRiderForm('temporaryPassword', event.target.value)
                  }
                  style={styles.formInput}
                />
              </label>
            </div>

            <p style={styles.formHint}>
              Give these credentials to the Rider securely. They can change the
              temporary password after login from Account.
            </p>
            <button
              type="submit"
              style={styles.submitButton}
              disabled={createLoading}
            >
              {createLoading ? 'Creating...' : 'Create Rider Account'}
            </button>
          </form>
        </div>
      )}

      {selectedRider && (
        <div style={styles.modalOverlay} onClick={() => setSelectedRider(null)}>
          <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <p style={styles.modalEyebrow}>Rider Profile</p>
                <h3 style={styles.modalTitle}>{selectedRider.name}</h3>
              </div>
              <button style={styles.closeButton} onClick={() => setSelectedRider(null)}>
                Close
              </button>
            </div>
            <div style={styles.detailGrid}>
              <span>Phone</span>
              <strong>{selectedRider.phone || 'Not provided'}</strong>
              <span>Email</span>
              <strong>{selectedRider.email || 'Not provided'}</strong>
              <span>Vehicle</span>
              <strong>{selectedRider.rider_type || 'Not provided'}</strong>
              <span>Licence</span>
              <div>
                {selectedRider.licenseUrl ? (
                  <>
                    <a
                      href={selectedRider.licenseUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.documentLink}
                    >
                      Open full licence
                    </a>
                    <img
                      src={selectedRider.licenseUrl}
                      alt={`${selectedRider.name} licence`}
                      style={styles.licencePreview}
                    />
                  </>
                ) : (
                  <strong>Not uploaded</strong>
                )}
              </div>
              <span>Account</span>
              <strong>{selectedRider.status}</strong>
              <span>Approval</span>
              <strong>
                {selectedRider.approvalStatus === 'approved'
                  ? 'Approved'
                  : selectedRider.approvalStatus === 'rejected'
                  ? 'Rejected'
                  : 'Pending'}
              </strong>
            </div>
          </div>
        </div>
      )}

      {editingRider && (
        <div style={styles.modalOverlay} onClick={() => setEditingRider(null)}>
          <form style={styles.modal} onSubmit={handleUpdateRider} onClick={(event) => event.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div><p style={styles.modalEyebrow}>Admin Only</p><h3 style={styles.modalTitle}>Edit Rider</h3></div>
              <button type="button" style={styles.closeButton} onClick={() => setEditingRider(null)}>Close</button>
            </div>
            <div style={styles.formGrid}>
              {[
                ['firstName', 'First name', 'text'],
                ['lastName', 'Last name', 'text'],
                ['email', 'Email', 'email'],
                ['phone', 'Phone', 'tel'],
              ].map(([field, label, type]) => (
                <label key={field} style={styles.formField}>
                  <span>{label}</span>
                  <input required type={type} value={editingRider[field]} onChange={(event) => setEditingRider(current => ({ ...current, [field]: event.target.value }))} style={styles.formInput} />
                </label>
              ))}
              <label style={styles.formField}>
                <span>Vehicle</span>
                <select value={editingRider.vehicleType} onChange={(event) => setEditingRider(current => ({ ...current, vehicleType: event.target.value }))} style={styles.formInput}>
                  <option value="motorcycle">Motorcycle</option><option value="bicycle">Bicycle</option><option value="car">Car</option>
                </select>
              </label>
            </div>
            <button type="submit" style={styles.submitButton} disabled={editLoading}>{editLoading ? 'Saving...' : 'Save Rider Changes'}</button>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        tone={confirmAction?.tone}
        loading={!!actionId}
        onCancel={() => setConfirmAction(null)}
        onConfirm={confirmAction?.onConfirm}
      />
    </Layout>
  );
}

const styles = {
  primaryActions: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
    marginBottom: 12,
  },
  secondaryActionButton: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    padding: '11px 16px',
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    fontWeight: 900,
    cursor: 'pointer',
  },
  createButton: {
    border: 'none',
    borderRadius: 6,
    padding: '11px 16px',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    fontWeight: 900,
    cursor: 'pointer',
  },
  toolbar: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 1fr) 170px 170px 150px',
    gap: 12,
    alignItems: 'center',
    marginBottom: 12,
  },
  searchInput: {
    padding: 10,
    width: '100%',
    fontSize: 16,
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    boxSizing: 'border-box',
  },
  selectInput: {
    padding: 10,
    width: '100%',
    fontSize: 14,
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.deepGreen,
    fontWeight: 800,
    boxSizing: 'border-box',
  },
  resultMeta: {
    color: COLORS.muted,
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 12,
  },
  emptyState: {
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 18,
    color: COLORS.deepGreen,
  },
  tableShell: {
    width: '100%',
    overflowX: 'auto',
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    boxShadow: '0 14px 32px rgba(15, 61, 46, 0.08)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: COLORS.offWhite,
    minWidth: 960,
  },
  thead: {
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    textAlign: 'left',
  },
  th: {
    padding: '15px 18px',
    fontWeight: 'bold',
  },
  td: {
    padding: '16px 18px',
    borderBottom: `1px solid ${COLORS.border}`,
    verticalAlign: 'middle',
    color: COLORS.deepGreen,
  },
  evenRow: {
    backgroundColor: COLORS.offWhite,
  },
  oddRow: {
    backgroundColor: COLORS.sand,
  },
  status: {
    color: '#fff',
    padding: '6px 12px',
    borderRadius: 12,
    fontSize: 12,
    display: 'inline-block',
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  actionBtn: {
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    padding: '7px 12px',
    border: 'none',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: '600',
    userSelect: 'none',
    lineHeight: 1,
    minHeight: 32,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(15, 61, 46, 0.12)',
  },
  documentButton: {
    border: `1px solid ${COLORS.forestGreen}`,
    borderRadius: 4,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    padding: '7px 10px',
    fontWeight: 800,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  missingDocument: {
    color: COLORS.danger,
    fontSize: 12,
    fontWeight: 800,
  },
  actionGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    minWidth: 306,
  },
  actionsHeader: {
    minWidth: 306,
  },
  paginationBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
    padding: '12px 0',
  },
  pageButton: {
    border: `1px solid ${COLORS.border}`,
    backgroundColor: COLORS.offWhite,
    color: COLORS.forestGreen,
    borderRadius: 6,
    padding: '8px 11px',
    fontWeight: 900,
    cursor: 'pointer',
  },
  pageText: {
    color: COLORS.muted,
    fontWeight: 800,
    fontSize: 13,
  },
  notice: {
    padding: '12px 14px',
    borderRadius: 8,
    marginBottom: 16,
    fontWeight: 800,
  },
  noticeSuccess: {
    backgroundColor: '#E7F6ED',
    color: COLORS.success,
    border: `1px solid ${COLORS.success}`,
  },
  noticeError: {
    backgroundColor: '#FCEAE8',
    color: COLORS.danger,
    border: `1px solid ${COLORS.danger}`,
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 61, 46, 0.42)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    zIndex: 1000,
  },
  modal: {
    width: 'min(560px, 100%)',
    backgroundColor: COLORS.offWhite,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    padding: 22,
    boxShadow: '0 24px 80px rgba(15, 61, 46, 0.22)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    marginBottom: 18,
  },
  modalEyebrow: {
    margin: 0,
    color: COLORS.heritageGold,
    fontWeight: 900,
    textTransform: 'uppercase',
    fontSize: 12,
  },
  modalTitle: {
    margin: '4px 0 0',
    color: COLORS.forestGreen,
    fontSize: 24,
  },
  closeButton: {
    alignSelf: 'flex-start',
    backgroundColor: COLORS.forestGreen,
    color: '#fff',
    border: 'none',
    borderRadius: 4,
    padding: '8px 12px',
    fontWeight: 800,
    cursor: 'pointer',
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '130px 1fr',
    gap: '12px 16px',
    color: COLORS.deepGreen,
  },
  documentLink: {
    display: 'inline-block',
    marginBottom: 10,
    color: COLORS.forestGreen,
    fontWeight: 900,
  },
  licencePreview: {
    display: 'block',
    width: '100%',
    maxWidth: 320,
    maxHeight: 220,
    objectFit: 'contain',
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    backgroundColor: '#fff',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 14,
  },
  formField: {
    display: 'grid',
    gap: 6,
    color: COLORS.deepGreen,
    fontSize: 13,
    fontWeight: 800,
  },
  formInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${COLORS.border}`,
    borderRadius: 6,
    backgroundColor: '#fff',
    color: COLORS.deepGreen,
    padding: '10px 11px',
    fontSize: 15,
  },
  formHint: {
    margin: '16px 0',
    color: COLORS.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },
  submitButton: {
    width: '100%',
    border: 'none',
    borderRadius: 6,
    padding: '12px 16px',
    backgroundColor: COLORS.heritageGold,
    color: COLORS.deepGreen,
    fontWeight: 900,
    cursor: 'pointer',
  },
};
